import { LoadingOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import moment from 'moment-timezone';
import {
  IconEye,
  IconDownload,
  IconResubmit,
} from '@nxsys/components/AppIconSvg';
import AppIconButton from '@nxsys/components/AppIconButton';
import { PsrRecordType } from '@nxsys/types/models/revenueFilings/psr';
import { formatMoney } from '@nxsys/utils/number-helper';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { formatStatus } from '@nxsys/utils';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { PaymentFrequencyEnum } from '@nxsys/constants/AppEnums';

type PSRColumnsProps = {
  handleClickView: (record: PsrRecordType) => void;
  handleClickDownload: (record: PsrRecordType) => void;
  handleExportError: (record: PsrRecordType) => void;
  handleResubmit: (record: PsrRecordType) => void;
  currencySymbol: string;
  messages: TypedMessages;
  downloadLoadingId?: string | null;
};

export const getPSRColumns = ({
  handleClickView,
  handleClickDownload,
  handleExportError,
  handleResubmit,
  currencySymbol,
  messages,
  downloadLoadingId,
}: PSRColumnsProps): ColumnsType<PsrRecordType> => [
  {
    key: 'actions',
    title: messages['table.actions'],
    fixed: 'left',
    align: 'left',
    width: 150,
    render: (record: PsrRecordType) => (
      <ul className="flex items-center justify-center gap-2">
        <li>
          <AppIconButton
            title={messages['common.view']}
            icon={<IconEye />}
            onClick={() => handleClickView(record)}
          />
        </li>
        <li
          className={downloadLoadingId === String(record.id) ? 'pointer-events-none text-primary-500' : ''}
          onClick={() => handleClickDownload(record)}
        >
          <Tooltip title={downloadLoadingId === String(record.id) ? 'Loading...' : messages['common.download']}>
            <span>
              {downloadLoadingId === String(record.id) ? (
                <LoadingOutlined style={{ fontSize: 16 }} spin />
              ) : (
                <IconDownload />
              )}
            </span>
          </Tooltip>
        </li>
        {!record?.isSuccess && (
          <>
            <li
              className={downloadLoadingId === `error-${record.id}` ? 'pointer-events-none text-primary-500' : ''}
              onClick={() => handleExportError(record)}
            >
              <Tooltip title={downloadLoadingId === `error-${record.id}` ? 'Loading...' : messages['common.downloadError']}>
                <span>
                  {downloadLoadingId === `error-${record.id}` ? (
                    <LoadingOutlined style={{ fontSize: 16 }} spin />
                  ) : (
                    <IconDownload color="red" />
                  )}
                </span>
              </Tooltip>
            </li>
            <li>
              <AppIconButton
                title={messages['common.resubmit']}
                icon={
                  <IconResubmit
                    color={defaultTheme.theme.palette.primary.main}
                  />
                }
                onClick={() => handleResubmit(record)}
              />
            </li>
          </>
        )}
      </ul>
    ),
  },
  {
    key: 'submissionID',
    title: messages['table.submissionID'],
    dataIndex: 'submissionID',
    width: 300,
    render: (value: string) => value || '__',
  },
  {
    key: 'type',
    title: messages['table.type'],
    align: 'center',
    width: 100,
    render: (record: PsrRecordType) =>
      record.leaverEmployment
        ? messages['common.leaver']
        : messages['common.normal'],
  },
  {
    key: 'status',
    title: messages['table.status'],
    align: 'center',
    width: 150,
    render: (record: PsrRecordType) => {
      let status = 'Failed';

      const hasPpsn =
        record?.payload?.payslips?.[0]?.employeeID?.employeePpsn || false;

      if (record.isSuccess && hasPpsn && !record.isResubmitted) {
        status = 'Success';
      } else if (record.isSuccess && hasPpsn && record.isResubmitted) {
        status = 'Updated';
      } else if (record.isSuccess && !hasPpsn) {
        status = 'Warning';
      } else if (!record.isSuccess) {
        status = 'Failed';
      }

      return (
        <span className={`${formatStatus(status)}`}>
          <AppTagStatus className={`tag-popup ${formatStatus(status)}`}>
            {formatStatus(status)}
          </AppTagStatus>
        </span>
      );
    },
  },
  {
    key: 'id',
    title: messages['table.systemId'],
    width: 150,
    render: (record: PsrRecordType) => record.subcontractor?.id || '__',
  },
  {
    key: 'employeeName',
    title: messages['table.employeeName'],
    width: 300,
    render: (record: PsrRecordType) => record.subcontractor?.fullName || '__',
  },
  {
    key: 'taxPeriod',
    title: messages['table.taxPeriod'],
    align: 'right',
    width: 150,
    render: (record: PsrRecordType) => {
      const payFrequency = record.payroll?.payFrequency;
      const _taxPeriod =
        payFrequency === PaymentFrequencyEnum.MONTHLY
          ? record.payroll?.monthConfig
          : record.payroll?.taxPeriod;
      const taxPeriod = _taxPeriod || record.payload?.payslips?.[0]?.payPeriod;
      const taxYear = record.payroll?.taxYear || record.taxYear;
      return taxPeriod && taxYear ? `${taxPeriod}/${taxYear}` : '_';
    },
  },
  {
    key: 'payFrequency',
    title: messages['table.payFrequency'],
    align: 'center',
    width: 150,
    render: (record: PsrRecordType) => {
      const frequency = defaultPaymentFrequency.find(
        (item) => item.value === record.payroll?.payFrequency,
      );
      return frequency?.label || '_';
    },
  },
  {
    key: 'ppsNumber',
    title: messages['table.ppsNumber'],
    width: 150,
    render: (record: PsrRecordType) =>
      record.payload?.payslips?.[0]?.employeeID?.employeePpsn || '__',
  },
  {
    key: 'employmentId',
    title: messages['table.employmentId'],
    width: 200,
    render: (record: PsrRecordType) =>
      record.payload?.payslips?.[0]?.employeeID?.employmentID || '__',
  },
  {
    key: 'employerReference',
    title: messages['table.employerReference'],
    width: 200,
    render: (record: PsrRecordType) => record.company?.companyName || '__',
  },
  {
    key: 'payDate',
    title: messages['table.payDate'],
    align: 'right',
    width: 200,
    render: (record: PsrRecordType) => {
      const payDate = record.payload?.payslips?.[0]?.payDate;
      return payDate ? moment(payDate).format('DD/MM/YYYY') : '_';
    },
  },
  {
    key: 'taxOnIncome',
    title: messages['table.incomeTax'],
    align: 'right',
    width: 150,
    render: (record: PsrRecordType) => {
      const tax = record.payload?.payslips?.[0]?.incomeTaxPaid || 0;
      return formatMoney(tax, currencySymbol);
    },
  },
  {
    key: 'prsi',
    title: messages['table.prsi'],
    align: 'right',
    width: 150,
    render: (record: PsrRecordType) => {
      const employeePRSI = record.payload?.payslips?.[0]?.employeePRSIPaid || 0;
      const employerPRSI = record.payload?.payslips?.[0]?.employerPRSIPaid || 0;
      const total = employeePRSI + employerPRSI;
      return formatMoney(total, currencySymbol);
    },
  },
  {
    key: 'usc',
    title: messages['table.usc'],
    align: 'right',
    width: 150,
    render: (record: PsrRecordType) => {
      const usc = record.payload?.payslips?.[0]?.uscPaid || 0;
      return formatMoney(usc, currencySymbol);
    },
  },
  {
    key: 'lpt',
    title: messages['table.lpt'],
    align: 'right',
    width: 150,
    render: (record: PsrRecordType) => {
      const lpt = record.payload?.payslips?.[0]?.lptDeducted || 0;
      return formatMoney(lpt, currencySymbol);
    },
  },
];
